import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Sector, Subject } from '../../types';
import {
  Plus,
  Edit2,
  Trash2,
  BookOpen,
  Brain,
  Calculator,
  Globe,
  Atom,
  Award,
  Compass,
  X,
  Layers,
  Sparkles,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';

const ICONS_LIST = [
  'BookOpen',
  'Brain',
  'Calculator',
  'Globe',
  'Atom',
  'Award',
  'Compass',
];

export const SectorSubjectManager: React.FC = () => {
  const {
    sectors,
    subjects,
    questionSets,
    addSector,
    updateSector,
    deleteSector,
    addSubject,
    updateSubject,
    deleteSubject,
  } = useApp();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'sectors' | 'subjects'>('sectors');
  const [deleteSectorTarget, setDeleteSectorTarget] = useState<Sector | null>(null);
  const [deleteSubjectTarget, setDeleteSubjectTarget] = useState<Subject | null>(null);

  const confirmDeleteSector = () => {
    if (!deleteSectorTarget) return;
    deleteSector(deleteSectorTarget.id);
    showToast('Sector deleted', 'info');
    setDeleteSectorTarget(null);
  };

  const confirmDeleteSubject = () => {
    if (!deleteSubjectTarget) return;
    deleteSubject(deleteSubjectTarget.id);
    showToast('Subject deleted', 'info');
    setDeleteSubjectTarget(null);
  };

  // Sector modal
  const [isSectorModalOpen, setIsSectorModalOpen] = useState(false);
  const [editingSector, setEditingSector] = useState<Sector | null>(null);
  const [sectorForm, setSectorForm] = useState({
    name: '',
    description: '',
    icon: 'Award',
    status: 'active' as 'active' | 'inactive',
  });

  // Subject modal
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [subjectForm, setSubjectForm] = useState({
    sector_id: sectors[0]?.id || 1,
    name: '',
    description: '',
    icon: 'BookOpen',
    status: 'active' as 'active' | 'inactive',
  });

  const handleOpenAddSector = () => {
    setEditingSector(null);
    setSectorForm({
      name: '',
      description: '',
      icon: 'Award',
      status: 'active',
    });
    setIsSectorModalOpen(true);
  };

  const handleOpenEditSector = (sec: Sector) => {
    setEditingSector(sec);
    setSectorForm({
      name: sec.name,
      description: sec.description,
      icon: sec.icon,
      status: sec.status,
    });
    setIsSectorModalOpen(true);
  };

  const handleSaveSector = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sectorForm.name.trim()) return;
    if (editingSector) {
      updateSector(editingSector.id, sectorForm);
      showToast('Sector updated successfully', 'success');
    } else {
      addSector(sectorForm);
      showToast('New examination sector created', 'success');
    }
    setIsSectorModalOpen(false);
  };

  const handleOpenAddSubject = () => {
    setEditingSubject(null);
    setSubjectForm({
      sector_id: sectors[0]?.id || 1,
      name: '',
      description: '',
      icon: 'BookOpen',
      status: 'active',
    });
    setIsSubjectModalOpen(true);
  };

  const handleOpenEditSubject = (sub: Subject) => {
    setEditingSubject(sub);
    setSubjectForm({
      sector_id: sub.sector_id,
      name: sub.name,
      description: sub.description,
      icon: sub.icon,
      status: sub.status,
    });
    setIsSubjectModalOpen(true);
  };

  const handleSaveSubject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subjectForm.name.trim()) return;
    if (editingSubject) {
      updateSubject(editingSubject.id, subjectForm);
      showToast('Subject updated successfully', 'success');
    } else {
      addSubject(subjectForm);
      showToast('New academic subject added', 'success');
    }
    setIsSubjectModalOpen(false);
  };

  return (
    <div className="space-y-6 pb-24 md:pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#111111] tracking-tight">Sectors & Subjects</h1>
          <p className="text-xs sm:text-sm text-[#5F6368]">
            Define exam streams, study domains, and individual academic subjects.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center bg-[#F0F4F4] p-1 rounded-xl border border-[#E5E9E9] self-start sm:self-auto">
          <button
            onClick={() => setActiveTab('sectors')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'sectors'
                ? 'bg-white text-[#111111] shadow-xs'
                : 'text-[#5F6368] hover:text-[#111111]'
            }`}
          >
            Sectors ({sectors.length})
          </button>
          <button
            onClick={() => setActiveTab('subjects')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'subjects'
                ? 'bg-white text-[#111111] shadow-xs'
                : 'text-[#5F6368] hover:text-[#111111]'
            }`}
          >
            Subjects ({subjects.length})
          </button>
        </div>
      </div>

      {/* Sectors Tab */}
      {activeTab === 'sectors' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={handleOpenAddSector}
              className="px-4 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4 text-[#111111]" />
              New Sector
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sectors.map((sec) => {
              const secSubjects = subjects.filter((s) => s.sector_id === sec.id);

              return (
                <div
                  key={sec.id}
                  className="bg-white rounded-2xl border border-[#E5E9E9] p-5 shadow-[0_2px_12px_rgba(17,17,17,0.02)] flex flex-col justify-between hover:border-[#56E8E1]/60 transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/30 flex items-center justify-center">
                        <Award className="w-5 h-5" />
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleOpenEditSector(sec)}
                          className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#1b8e88] hover:bg-[#E8FAF9] transition-colors cursor-pointer"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteSectorTarget(sec)}
                          className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <h3 className="text-base font-bold text-[#111111] mb-1">{sec.name}</h3>
                    <p className="text-xs text-[#5F6368] mb-4 line-clamp-2 leading-relaxed">
                      {sec.description || 'No description provided.'}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-[#E5E9E9] flex items-center justify-between text-xs text-[#5F6368] font-medium">
                    <span>{secSubjects.length} Associated Subjects</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#EAF8F1] text-[#20A66A] font-bold text-[10px] uppercase border border-[#20A66A]/30">
                      {sec.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Subjects Tab */}
      {activeTab === 'subjects' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={handleOpenAddSubject}
              className="px-4 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4 text-[#111111]" />
              New Subject
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {subjects.map((sub) => {
              const parentSector = sectors.find((sec) => sec.id === sub.sector_id);
              const subSets = questionSets.filter((qs) => qs.subject_id === sub.id);

              return (
                <div
                  key={sub.id}
                  className="bg-white rounded-2xl border border-[#E5E9E9] p-5 shadow-[0_2px_12px_rgba(17,17,17,0.02)] flex flex-col justify-between hover:border-[#EBC138]/60 transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40 flex items-center justify-center">
                        <BookOpen className="w-5 h-5" />
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleOpenEditSubject(sub)}
                          className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#8a6508] hover:bg-[#FFF7D9] transition-colors cursor-pointer"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteSubjectTarget(sub)}
                          className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="px-2 py-0.5 rounded-md bg-[#F0F4F4] text-[#5F6368] text-[10px] font-semibold border border-[#E5E9E9]">
                        {parentSector?.name || 'Unassigned'}
                      </span>
                    </div>

                    <h3 className="text-base font-bold text-[#111111] mb-1">{sub.name}</h3>
                    <p className="text-xs text-[#5F6368] mb-4 line-clamp-2 leading-relaxed">
                      {sub.description || 'No description provided.'}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-[#E5E9E9] flex items-center justify-between text-xs text-[#5F6368] font-medium">
                    <span>{subSets.length} Question Sets</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#EAF8F1] text-[#20A66A] font-bold text-[10px] uppercase border border-[#20A66A]/30">
                      {sub.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sector Modal */}
      {isSectorModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-[#E5E9E9]">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
              <h3 className="text-base font-bold text-[#111111]">
                {editingSector ? 'Edit Sector' : 'New Sector'}
              </h3>
              <button
                onClick={() => setIsSectorModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveSector} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Sector Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Competitive Exams, Civil Services..."
                  value={sectorForm.name}
                  onChange={(e) => setSectorForm({ ...sectorForm, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Description</label>
                <textarea
                  rows={3}
                  placeholder="Describe the category, candidate eligibility, or curriculum..."
                  value={sectorForm.description}
                  onChange={(e) =>
                    setSectorForm({ ...sectorForm, description: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Status</label>
                <select
                  value={sectorForm.status}
                  onChange={(e) =>
                    setSectorForm({
                      ...sectorForm,
                      status: e.target.value as 'active' | 'inactive',
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#E5E9E9]">
                <button
                  type="button"
                  onClick={() => setIsSectorModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {editingSector ? 'Save Changes' : 'Create Sector'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subject Modal */}
      {isSubjectModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-[#E5E9E9]">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
              <h3 className="text-base font-bold text-[#111111]">
                {editingSubject ? 'Edit Subject' : 'New Subject'}
              </h3>
              <button
                onClick={() => setIsSubjectModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveSubject} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Parent Sector</label>
                <select
                  value={subjectForm.sector_id}
                  onChange={(e) =>
                    setSubjectForm({ ...subjectForm, sector_id: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                >
                  {sectors.map((sec) => (
                    <option key={sec.id} value={sec.id}>
                      {sec.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Subject Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Logical Reasoning, General Science..."
                  value={subjectForm.name}
                  onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Description</label>
                <textarea
                  rows={2}
                  placeholder="Brief summary of syllabus or core topics..."
                  value={subjectForm.description}
                  onChange={(e) =>
                    setSubjectForm({ ...subjectForm, description: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">Icon Representation</label>
                <select
                  value={subjectForm.icon}
                  onChange={(e) => setSubjectForm({ ...subjectForm, icon: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                >
                  {ICONS_LIST.map((ic) => (
                    <option key={ic} value={ic}>
                      {ic}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#E5E9E9]">
                <button
                  type="button"
                  onClick={() => setIsSubjectModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {editingSubject ? 'Save Changes' : 'Create Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteSectorTarget}
        title="Delete Sector"
        message={
          deleteSectorTarget
            ? `Delete sector "${deleteSectorTarget.name}"? This will not delete subjects directly.`
            : ''
        }
        confirmText="Delete Sector"
        variant="danger"
        onConfirm={confirmDeleteSector}
        onCancel={() => setDeleteSectorTarget(null)}
      />

      <ConfirmModal
        isOpen={!!deleteSubjectTarget}
        title="Delete Subject"
        message={
          deleteSubjectTarget
            ? `Delete subject "${deleteSubjectTarget.name}"?`
            : ''
        }
        confirmText="Delete Subject"
        variant="danger"
        onConfirm={confirmDeleteSubject}
        onCancel={() => setDeleteSubjectTarget(null)}
      />
    </div>
  );
};
